import React, {
  useEffect,
  useState,
  useContext,
  useCallback
} from 'react';
import PropTypes from 'prop-types';
import {
  useLazyDataModel,
  extend,
  params,
  shape,
  string,
  number,
  arrayOf,
  customType
} from '@thd-nucleus/data-sources';
import {
  Card,
  CardBody,
  Typography,
  Badge,
  Button,
  LoadingButton,
  TextField,
  Radio,
  RadioGroup,
  SelectionControlLabel,
} from '@one-thd/sui-atomic-components';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { TapToSpeak } from '../tap-to-speak/TapToSpeak';
import { AddToContext } from '../../AddToContext';
import { SCREENS, TRACK } from '../../AddToConstants';
import { formatMaterials, triggerAnalytics } from '../../AddToHelpers';

/**
 * AI List Builder component for describing projects and generating lists
 */
export const AIListBuilder = ({
  name,
  materials,
  hideTitle,
  hideButtons,
  refType,
  refId,
}) => {
  const TAB_NAMES = {
    DESCRIBE: 'describe',
    PASTE: 'paste'
  };
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const [aiErrors, setAiErrors] = useState('');
  const [inputTypeByTab, setInputTypeByTab] = useState({
    [TAB_NAMES.DESCRIBE]: 'TEXT',
    [TAB_NAMES.PASTE]: 'TEXT'
  });

  const {
    setGroupedIntentsPreview,
    setScreen,
    screen,
    aiPrompt,
    setAiPrompt,
    aiSelectedTab,
    setAiSelectedTab,
    mcvisId
  } = useContext(AddToContext);

  // Handle speech-to-text transcript changes
  const handleTranscriptChange = useCallback((transcript) => {
    if (transcript) {
      setInputTypeByTab((prev) => ({
        ...prev,
        [aiSelectedTab]: 'VOICE_TO_TEXT'
      }));
      setAiPrompt({
        tab: aiSelectedTab,
        text: transcript
      });
    }
  }, [aiSelectedTab, setAiPrompt]);

  // useLazyDataModel for AI agent interaction
  const [sendMessage, response] = useLazyDataModel('interactWithAgent', {
    variables: {},
    context: {
      withAuth: true
    }
  });

  const { data, loading, error } = response || {};

  // Set default tab to DESCRIBE when buttons are hidden
  useEffect(() => {
    if (hideButtons && aiSelectedTab !== TAB_NAMES.DESCRIBE) {
      setAiSelectedTab(TAB_NAMES.DESCRIBE);
    }
  }, [hideButtons, aiSelectedTab, setAiSelectedTab]);

  useEffect(() => {
    const isStarterListsOverlay = screen === SCREENS.STARTER_LISTS;
    const pageSection = isStarterListsOverlay ? 'starter lists overlay' : 'add to list overlay';

    if (data) {
      const groupedIntents = formatMaterials(data?.interactWithAgent?.materialsGroups);
      if (data?.interactWithAgent?.error) {
        // 6.12.2 Post-Page Error Tracking for AI
        const errorCode = data.interactWithAgent.errorCode || 'n/a';

        triggerAnalytics(TRACK.LIST_BUILDER_ERROR, {
          errorType: 'error', // AI generation failures are business logic errors, not system failures
          errorMessage: data.interactWithAgent.error,
          errorCode,
          pageSection
        });

        setAiErrors(data.interactWithAgent.error);
      } else {
        // Determine the input method based on tab and speech usage
        const inputMethod = aiSelectedTab === TAB_NAMES.DESCRIBE ? 'describe project' : 'paste list';
        const inputTypeForAnalytics = inputTypeByTab[aiSelectedTab] === 'VOICE_TO_TEXT' ? 'speech to text' : 'text';
        const sessionId = data?.interactWithAgent?.metadata?.sessionId;
        // 10.2.1 & 10.3 List Selection - Track custom entry (text or speech to text) with sessionId
        const selection = isStarterListsOverlay
          ? `custom entry: ${inputTypeForAnalytics}`
          : inputTypeForAnalytics;
        const analyticsEvent = isStarterListsOverlay
          ? TRACK.LIST_BUILDER_STARTER_LIST_SELECTION : TRACK.LIST_BUILDER_BUILD_WITH_AI_SELECTION;

        triggerAnalytics(analyticsEvent, {
          aiSessionId: sessionId,
          aiTextEntry: aiPrompt.text,
          aiType: inputMethod,
          selection
        });

        const generatedMaterials = {
          sessionId,
          groupedIntents,
          sourceType: 'ai',
          aiInputMethod: inputMethod,
          aiInputType: inputTypeForAnalytics
        };
        setGroupedIntentsPreview(generatedMaterials);
        setScreen(SCREENS.GENERATED_LIST_INTENTS_PREVIEW);
      }
    }
    if (error) {
      // 6.12.2 Post-Page Error Tracking for AI - Network/API errors
      const errorCode = error.code || error.status || 'n/a';

      triggerAnalytics(TRACK.LIST_BUILDER_ERROR, {
        errorType: 'system', // Network/API failures are system errors
        errorMessage: error.message || 'Network error occurred',
        errorCode,
        pageSection
      });

      // Handle error appropriately - could set error state or show user message
      // eslint-disable-next-line no-console
      setAiErrors('We\'re sorry, something went wrong. Please try again.');
    }
  }, [data, error, setGroupedIntentsPreview, setScreen, aiSelectedTab, inputTypeByTab]);

  const onListeningChange = (isListening) => {
    if (isListening) {
      setInputTypeByTab((prev) => ({
        ...prev,
        [aiSelectedTab]: 'VOICE_TO_TEXT'
      }));
    }
  };

  const handleTabChange = (event) => {
    const { value } = event?.target;
    // 10.1 AI Entry Points - Track AI entry point clicks
    const entryPointName = value === TAB_NAMES.DESCRIBE ? 'describe project' : 'paste list';
    triggerAnalytics(TRACK.LIST_BUILDER_ENTRY_POINT_CLICK, {
      entryPoint: entryPointName
    });
    setAiSelectedTab(value);
  };

  const handleDescriptionChange = (event) => {
    const newText = event.target.value;
    // If the user completely clears the field, reset input type for this tab
    if (!newText.trim()) {
      setInputTypeByTab((prev) => ({
        ...prev,
        [aiSelectedTab]: 'TEXT'
      }));
    }
    setAiPrompt({
      text: newText,
      tab: aiSelectedTab
    });
    if (aiErrors) {
      setAiErrors('');
    }
  };

  const handleGenerate = () => {
    if (aiPrompt.text.trim()) {
      if (aiErrors) {
        setAiErrors('');
      }
      // Set the flag to allow the query to fire and trigger the AI agent interaction
      sendMessage({
        variables: {
          interactionType: 'MESSAGE',
          agent: 'DEFAULT',
          template: aiSelectedTab === TAB_NAMES.DESCRIBE ? 'PROJECT_DESCRIPTION' : 'MATERIALS_TO_LIST',
          message: aiPrompt.text,
          templateData: [
            { key: 'materialsGroup', value: JSON.stringify(materials || []) },
            { key: 'title', value: name }
          ],
          metaData: {
            refType,
            refId,
            channel,
            mcvis: mcvisId || '',
            inputType: inputTypeByTab[aiSelectedTab]
          }
        }
      });
    }
  };
  const PLACEHOLDER_TEXT = `e.g.
  • 50 2x4's
  • 10 2x8's
  • 5 electrical boxes
  • drywall screws`;

  // Dynamic text content based on active tab
  const getTextContent = () => {
    if (aiSelectedTab === TAB_NAMES.PASTE) {
      return {
        description: 'Paste your list and we\'ll help you find the right products.',
        placeholder: PLACEHOLDER_TEXT,
        speechButtonText: 'Dictate list'
      };
    }

    return {
      description: 'Describe your project or what you need materials for. '
        + 'We\'ll turn your description into a customizable list.',
      placeholder: 'e.g. Building a 10 x 10 deck with composite boards, railing, and concrete footings.',
      speechButtonText: 'Tap to speak'
    };
  };

  const textContent = getTextContent();

  return (
    <Card className="sui-w-full sui-bg-subtle">
      <CardBody>
        {/* Header with Icon, Title, and Beta Badge */}
        {!hideTitle && (
          <div className="sui-flex sui-items-center sui-justify-center sui-mb-4">
            <img
              src="https://assets.thdstatic.com/images/v1/ai-sparkle-transparent.svg"
              alt="AI Sparkle"
              className="sui-mr-2"
              width="24"
              height="24"
            />
            <div className="sui-mr-2">
              <Typography variant="h3" align="center" weight="display">
                Build your list with AI
              </Typography>
            </div>
            <Badge color="info" variant="regular" size="small">
              Beta
            </Badge>
          </div>
        )}
        {/* Tab Navigation */}
        {!hideButtons && (
          <div className="sui-mb-4">
            <RadioGroup
              aria-labelledby="ai-list-builder-input-type"
              name="ai-input-type"
              value={aiSelectedTab}
              onChange={handleTabChange}
            >
              <div className="sui-mb-3">
                <SelectionControlLabel value={TAB_NAMES.DESCRIBE} label="Describe Project">
                  <Radio />
                </SelectionControlLabel>
                <div className="sui-ml-8">
                  <Typography variant="body-sm" color="subtle">
                    Describe your project or what you need materials for.
                    We&apos;ll turn your description into a customizable list.
                  </Typography>
                </div>
              </div>
              <div>
                <SelectionControlLabel value={TAB_NAMES.PASTE} label="Paste List">
                  <Radio />
                </SelectionControlLabel>
                <div className="sui-ml-8">
                  <Typography variant="body-sm" color="subtle">
                    Paste your list and we&apos;ll help you find the right products.
                  </Typography>
                </div>
              </div>
            </RadioGroup>
          </div>
        )}

        {/* Description Text - Only show when hideButtons is true */}
        {hideButtons && (
          <div className="sui-mt-2 sui-mb-4">
            <Typography variant="body-base" align="center">
              {textContent.description}
            </Typography>
          </div>
        )}

        {/* Text Area with Speech Button */}
        <div className="sui-mb-6">
          <TextField
            placeholder={textContent.placeholder}
            multiline
            minRows={8}
            maxRows={8}
            fullWidth
            value={aiPrompt.tab === aiSelectedTab ? aiPrompt.text : ''}
            onChange={handleDescriptionChange}
            inputProps={{ 'aria-label': 'Project description input' }}
            status={aiErrors ? 'error' : undefined}
            statusMessage={aiErrors}
          />
        </div>

        {/* Generate and Speech Buttons */}
        <div className="sui-flex sui-gap-2 sui-w-full">
          <LoadingButton
            data-testid="ai-generate-button"
            variant={!aiPrompt.text.trim() ? 'secondary' : 'primary'}
            onClick={handleGenerate}
            disabled={!aiPrompt.text.trim()}
            loading={loading}
            fullWidth
          >
            Generate
          </LoadingButton>
          {!isConsumerApp && (
            <TapToSpeak
              onTranscriptChange={handleTranscriptChange}
              disabled={loading}
              defaultText={textContent.speechButtonText}
              onListeningChange={onListeningChange}
            />
          )}
        </div>
      </CardBody>
    </Card>
  );
};

AIListBuilder.displayName = 'AIListBuilder';

AIListBuilder.propTypes = {
  name: PropTypes.string,
  materials: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string
  })),
  hideTitle: PropTypes.bool,
  hideButtons: PropTypes.bool,
  refType: PropTypes.string,
  refId: PropTypes.string,
};

AIListBuilder.defaultProps = {
  name: '',
  materials: [],
  hideTitle: false,
  hideButtons: false,
  refType: '',
  refId: '',
};

AIListBuilder.dataModel = extend({
  interactWithAgent: params({
    agent: customType('Agent').enum(['DEFAULT', 'VERTEX_AI']),
    interactionType: customType('InteractionType').enum(['MESSAGE', 'FEEDBACK']).isRequired(),
    message: string(),
    template: customType('TemplateType').enum(['PROJECT_DESCRIPTION', 'MATERIALS_TO_LIST']),
    templateData: arrayOf(
      customType('TemplateData')
        .shape({
          key: string(),
          value: string()
        })
    ),
    metaData: customType('MetadataInput').shape({
      refType: string(),
      refId: string(),
      channel: string(),
      mcvis: string(),
      inputType: string()
    })
  }).mutation().shape({
    metadata: shape({
      inputTokenCount: number(),
      sessionId: string(),
      statusMessage: string()
    }),
    error: string(),
    materialsGroups: arrayOf(
      shape({
        groupName: string(),
        materials: arrayOf(
          shape({
            intent: string(),
            quantity: number()
          })
        )
      })
    )
  })
});
